/**
 * VendorsListPage — G-1. 자재파트너 목록
 *
 * 레이아웃은 나의 사진첩(현장 목록)과 같은 형식 — PageHeader + 검색 영역 카드 + 카드 그리드(4열).
 *   - 키워드: 서버 조회(fetchVendors(keyword)) — SearchCombo(D-09 §15.13)
 *   - 등록일 기간: 클라이언트 필터 — DateRangePicker(§15.14)
 *   - 전문분야: 클라이언트 필터, 다중선택(OR 매칭) — FilterChip
 */
import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Badge, Button, DateRangePicker, EmptyState, ErrorState, FilterChip,
  Page, PageHeader, SearchCombo, Skeleton, Tag,
} from '@indipia/ui-core';
import { fetchVendorMetrics, fetchVendors, updateVendor } from '../api/vendors';
import type { VendorItem, VendorMetrics } from '../api/vendors';
import { fetchWorkTypes } from '../api/work-types';
import { useDateRangeFilter } from '@indipia/shared';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { PartnerReviewDrawer } from '../../app/shell/PartnerReviewDrawer';
import {
  InfoList, InfoRow, ListCard, ListCardActions, ListCardGrid, ListCardHeader, ListCardIdentity, ListCardMetrics, StarRating,
} from '../components/ListCard';

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '-';
  const d = new Date(iso);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
}

function IcVendor() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
      <path d="M3.27 6.96 12 12.01l8.73-5.05"/>
      <path d="M12 22.08V12"/>
    </svg>
  );
}

// 활동지역(배송지역) 요약 — 시군구명("서울특별시 강남구")에서 시·도만 축약("서울"), 유니크 묶음.
function regionSummary(names: string[]): string {
  const sidos = Array.from(new Set(names.map((n) => {
    const head = (n || '').split(' ')[0];
    return head.replace(/(특별자치시|특별자치도|특별시|광역시|도)$/, '');
  }).filter(Boolean)));
  if (sidos.length === 0) return '-';
  return sidos.join(' · ');
}

// ── 메인 컴포넌트 ────────────────────────────────────────────────────────────

// 전문분야 필터의 "미지정"(취급품목 없음) 선택용 센티넬 — 실제 work-type 코드와 겹치지 않는 값
const NONE_CODE = '__NONE__';

export function VendorsListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deleteTarget, setDeleteTarget] = useState<VendorItem | null>(null);
  const [reviewTarget, setReviewTarget] = useState<VendorItem | null>(null);

  const { data: workTypeList = [] } = useQuery({
    queryKey: ['work-types'],
    queryFn: fetchWorkTypes,
  });
  const codeNameMap = useMemo(
    () => new Map(workTypeList.map((w) => [w.workTypeCode, w.workTypeName])),
    [workTypeList],
  );

  // 검색(서버) — 입력 디바운스로 실시간 조회, 버튼/Enter는 즉시
  const [searchInput, setSearchInput] = useState('');
  const [keyword,     setKeyword]     = useState('');
  const debouncedSearch = useDebouncedValue(searchInput, 300);
  useEffect(() => { setKeyword(debouncedSearch.trim()); }, [debouncedSearch]);

  // 등록일 기간 필터(클라이언트, createdAt 기준) — 기본 미지정(전체)
  const { dateRange, setDateRange, dateFrom, dateTo, reset: resetDateRange } = useDateRangeFilter();

  // 전문분야 필터(클라이언트, 다중선택 OR 매칭)
  const [selectedSpecialties, setSelectedSpecialties] = useState<string[]>([]);

  const { data: vendors = [], isLoading, isError, refetch } = useQuery({
    queryKey: ['vendors', keyword],
    queryFn: () => fetchVendors(keyword || undefined),
  });

  // 발주회신·납기준수·양중가능 지표와 거래 횟수 — 일정 서비스(sv-schedule) 집계를 vendorId 로 병합.
  // 조회 실패 시 % 지표는 모두 생략하고(0 으로 채우지 않음) 목록은 평점·거래 횟수만 정상 표시한다.
  const { data: metricList, isError: metricsFailed } = useQuery({
    queryKey: ['vendor-metrics'],
    queryFn: fetchVendorMetrics,
    retry: 1,
  });
  const metricsById = useMemo(() => {
    const map = new Map<number, VendorMetrics>();
    for (const m of metricList ?? []) map.set(m.vendorId, m);
    return map;
  }, [metricList]);

  const deleteMutation = useMutation({
    mutationFn: (vendorId: number) => updateVendor(vendorId, { action: 'delete' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      setDeleteTarget(null);
    },
  });

  // ── 클라이언트 필터 (등록일 + 전문분야) ───────────────────────────────────
  const filtered = useMemo(() => vendors.filter((v) => {
    if ((dateFrom || dateTo) && v.createdAt) {
      const d = v.createdAt.slice(0, 10);
      if (dateFrom && d < dateFrom) return false;
      if (dateTo   && d > dateTo)   return false;
    }
    if (selectedSpecialties.length > 0) {
      const codes = v.workTypes ?? [];
      const matchCode = codes.some((c) => selectedSpecialties.includes(c));
      const matchNone = selectedSpecialties.includes(NONE_CODE) && codes.length === 0;
      if (!matchCode && !matchNone) return false;
    }
    return true;
  }), [vendors, dateFrom, dateTo, selectedSpecialties]);

  const hasActiveFilters = !!keyword || !!dateFrom || !!dateTo || selectedSpecialties.length > 0;

  function clearFilters() {
    setKeyword('');
    setSearchInput('');
    resetDateRange();
    setSelectedSpecialties([]);
  }

  function toggleSpecialty(code: string) {
    setSelectedSpecialties((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );
  }

  function workTypeNames(codes: string[]): string[] {
    return codes.map((c) => codeNameMap.get(c) ?? c);
  }

  return (
    <Page width="xl">
      <PageHeader
        title="자재파트너 목록"
        subtitle="등록된 자재파트너를 확인하고 관리하세요"
        extra={
          <Button type="button" variant="primary" onClick={() => navigate('/vendors/new')}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            신규 등록
          </Button>
        }
      />

      <div className="flex flex-col gap-5">

        {/* 검색 영역 — 나의 사진첩 현장 목록과 같은 카드 묶음 (①키워드 ②등록일 기간 ③전문분야) */}
        <div className="flex flex-col gap-2.5 rounded-xl border border-border bg-surface-raised p-5">
          <div className="flex flex-wrap items-center gap-2">
            <SearchCombo
              className="min-w-60 flex-1"
              value={searchInput}
              onChange={(v) => setSearchInput(v.slice(0, 50))}
              onSearch={() => setKeyword(searchInput.trim())}
              onClear={() => { setSearchInput(''); setKeyword(''); }}
              maxLength={50}
              placeholder="거래처명 · 대표자명 · 연락처 검색"
              aria-label="자재파트너 검색"
            />
            <DateRangePicker
              value={dateRange}
              onChange={setDateRange}
              placeholder="등록일 전체"
              aria-label="등록일 기간"
            />
            {hasActiveFilters && (
              <Button type="button" variant="etc" onClick={clearFilters} className="shrink-0">
                초기화
              </Button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            <span className="mr-1 shrink-0 text-caption font-semibold text-text-secondary">전문분야</span>
            <FilterChip active={selectedSpecialties.length === 0} onClick={() => setSelectedSpecialties([])}>
              전체
            </FilterChip>
            <FilterChip active={selectedSpecialties.includes(NONE_CODE)} onClick={() => toggleSpecialty(NONE_CODE)}>
              미지정
            </FilterChip>
            {workTypeList.map((w) => (
              <FilterChip
                key={w.workTypeCode}
                active={selectedSpecialties.includes(w.workTypeCode)}
                onClick={() => toggleSpecialty(w.workTypeCode)}
              >
                {w.workTypeName}
              </FilterChip>
            ))}
          </div>

          <p className="flex items-center gap-1 text-caption text-text-muted">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            기간 검색 시 거래처 등록일 기준으로 검색됩니다.
          </p>
        </div>

        {/* 로딩 */}
        {isLoading && (
          <ListCardGrid>
            {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-48 rounded-xl" />)}
          </ListCardGrid>
        )}

        {!isLoading && isError && (
          <ErrorState description="자재파트너 목록을 불러오지 못했어요. 잠시 후 다시 시도해 주세요." onRetry={() => void refetch()} />
        )}

        {/* 빈 상태 */}
        {!isLoading && !isError && filtered.length === 0 && hasActiveFilters && (
          <EmptyState
            title="조회 결과가 없습니다"
            description="검색어·기간·전문분야 조건에 맞는 거래처가 없어요."
            action={<Button type="button" variant="secondary" onClick={clearFilters}>필터 초기화</Button>}
          />
        )}

        {!isLoading && !isError && filtered.length === 0 && !hasActiveFilters && (
          <EmptyState
            icon={<IcVendor />}
            title="등록된 자재파트너가 없습니다"
            description="첫 거래처를 등록하고 자재 파트너를 관리해 보세요."
            action={<Button type="button" variant="primary" onClick={() => navigate('/vendors/new')}>+ 첫 거래처 등록</Button>}
          />
        )}

        {/* 카드 그리드 — 4열 고정 */}
        {!isLoading && !isError && filtered.length > 0 && (
          <ListCardGrid>
            {filtered.map((vendor) => {
              const tags = workTypeNames(vendor.workTypes ?? []);
              // 거래 횟수·% 지표는 일정 서비스 집계 병합값(거래 횟수는 집계 tradeCount 우선, % 가 null 이면 그 항목만 생략).
              // 집계 조회가 끝나기 전이거나 실패했을 때는 % 지표를 모두 생략하고 평점·거래 횟수(회원 서비스 값)만 보인다.
              // 집계 조회가 끝났는데 이 거래처 행이 없으면 거래 이력이 없는 것으로 본다.
              const metrics = metricsById.get(vendor.vendorId);
              const mergeDone = metricList !== undefined && !metricsFailed;
              const orderCount = metrics ? metrics.tradeCount : (vendor.orderCount ?? 0);
              const hasHistory = mergeDone ? !!metrics : (orderCount > 0 || (vendor.reviewCount ?? 0) > 0);
              const region = regionSummary((vendor.sigungus ?? []).map((s) => s.sigunguName));
              const badges = [
                vendor.commYn === 'N' && <Badge key="comm" status="warning">알림톡 수신 안 함</Badge>,
              ];
              const hasBadge = badges.some(Boolean);

              return (
                <ListCard key={vendor.vendorId}>
                  <ListCardHeader
                    badges={badges}
                    title={vendor.vendorName}
                    editLabel="거래처 편집"
                    deleteLabel="거래처 삭제"
                    onEdit={() => navigate(`/vendors/${vendor.vendorId}/edit`, { state: { vendor } })}
                    onDelete={() => setDeleteTarget(vendor)}
                  />

                  <ListCardIdentity name={vendor.vendorName} hideName={!hasBadge} phone={vendor.vendorPhone} />

                  {/* 취급품목 태그 */}
                  {tags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {tags.map((name) => <Tag key={name}>{name}</Tag>)}
                    </div>
                  )}

                  {/* 지표(★평점·거래·발주회신·납기준수·양중가능, 값 없는 % 는 생략) */}
                  <ListCardMetrics>
                    {hasHistory ? (
                      <>
                        <span className="flex flex-wrap items-center gap-x-4">
                          {vendor.avgRating != null && vendor.avgRating > 0 && (
                            <StarRating rating={vendor.avgRating} count={vendor.reviewCount ?? 0} />
                          )}
                          <span className="text-point">거래 <b className="font-bold">{orderCount}회</b></span>
                        </span>
                        {metrics?.replyWithinTwoHoursPercent != null && (
                          <span>발주회신 2시간 내 <b className="font-bold text-text">{metrics.replyWithinTwoHoursPercent}%</b></span>
                        )}
                        {metrics?.onTimeDeliveryPercent != null && (
                          <span>납기준수 <b className="font-bold text-text">{metrics.onTimeDeliveryPercent}%</b></span>
                        )}
                        {metrics?.liftAvailPercent != null && (
                          <span>양중가능 <b className="font-bold text-text">{metrics.liftAvailPercent}%</b></span>
                        )}
                      </>
                    ) : (
                      <span>아직 거래 이력이 없어요</span>
                    )}
                  </ListCardMetrics>

                  <InfoList>
                    <InfoRow label="대표자">{vendor.vendorRepName || '-'}</InfoRow>
                    <InfoRow label="담당자">{vendor.contactName || '-'}</InfoRow>
                    <InfoRow label="활동지역">{region}</InfoRow>
                    <InfoRow label="등록일">{formatDate(vendor.createdAt)}</InfoRow>
                    <InfoRow label="최근거래현장">
                      {vendor.lastOrderSiteName
                        ? `${vendor.lastOrderSiteName} (${formatDate(vendor.lastOrderDate)})`
                        : '-'}
                    </InfoRow>
                  </InfoList>

                  <ListCardActions>
                    <Button type="button" variant="secondary" size="sm" onClick={() => setReviewTarget(vendor)} className="col-span-2 w-full">
                      거래이력 &amp; 평가 메모
                    </Button>
                  </ListCardActions>
                </ListCard>
              );
            })}
          </ListCardGrid>
        )}
      </div>

      {/* 삭제 확인 모달 */}
      {deleteTarget && (
        <ConfirmDialog
          open
          message={`${deleteTarget.vendorName}을(를) 삭제하시겠습니까?`}
          description="삭제 후 복구할 수 없습니다."
          pendingLabel="삭제 중…"
          isPending={deleteMutation.isPending}
          onConfirm={() => deleteMutation.mutate(deleteTarget.vendorId)}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      {/* 거래이력·평가 슬라이드오버 (M1/M2) */}
      {reviewTarget && (
        <PartnerReviewDrawer
          key={reviewTarget.vendorId}
          kind="vendor"
          id={reviewTarget.vendorId}
          name={reviewTarget.vendorName}
          onClose={() => setReviewTarget(null)}
        />
      )}
    </Page>
  );
}

